import { Component, ChangeDetectionStrategy, Output, EventEmitter, signal, computed, effect, input, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';

export interface LocationDto {
    id: string;
    appNamespaceId: string;
    parentId?: string | null;
    name: string;
    code?: string;
    type?: string | null;
}

/**
 * Tree picker used by the "move device" / "move location" dialogs.
 *
 * p-tree keeps a node's open/closed state on the `TreeNode.expanded` property it mutates in
 * place - it has no expandedKeys input - so handing it a freshly built node array collapses the
 * whole tree and drops the selection. Callers derive the `locations` array from live signals
 * (e.g. a room's details object, which is replaced every time its processor checks in), so the
 * input identity changes constantly even when the locations themselves are untouched.
 *
 * The tree is therefore rebuilt only when the locations that make it up actually change
 * (see `treeSignature`), and expansion is remembered by key so it survives a genuine rebuild.
 */
@Component({
    selector: 'app-location-picker',
    standalone: true,
    imports: [CommonModule, TreeModule],
    templateUrl: './location-picker.component.html',
    styleUrls: ['./location-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationPickerComponent {
    // Input signals
    locations = input<LocationDto[]>([]);
    namespaceId = input<string | null>(null);
    currentLocationId = input<string | null>(null);
    disabled = input<boolean>(false);
    minHeight = input<string>('500px');
    maxHeight = input<string>('600px');

    // Output events
    @Output() locationSelected = new EventEmitter<string | null>();

    // Internal state
    selectedLocationNode = signal<TreeNode | null>(null);

    /** Id of the picked location. Survives tree rebuilds; the node object is resolved from it. */
    private selectedKey = signal<string | null>(null);

    /**
     * Keys of the nodes that are open. Held outside the signal graph on purpose: recording a
     * toggle must not invalidate the tree and re-create the very nodes p-tree is mutating.
     */
    private expandedKeys = new Set<string>();

    /** Locations in scope, in a stable (name) order so sibling order never depends on fetch order. */
    private scopedLocations = computed(() => {
        const locs = this.locations() ?? [];
        const nsId = this.namespaceId();
        const scoped = nsId ? locs.filter((l) => l.appNamespaceId === nsId) : [...locs];
        return scoped.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    /**
     * Fingerprint of everything the tree renders. Recomputing it is cheap and its value only
     * changes when a location is added, removed, renamed, retyped or reparented - so the tree
     * computed below (and with it every TreeNode object) is left alone on unrelated churn.
     */
    private treeSignature = computed(() =>
        this.scopedLocations()
            .map((l) => `${l.id}\u0001${l.parentId ?? ''}\u0001${l.name}\u0001${l.type ?? ''}`)
            .join('\u0002')
    );

    // Computed location tree
    locationTree = computed<TreeNode[]>(() => {
        this.treeSignature();
        return untracked(() => this.buildTree());
    });

    constructor() {
        // Reset the pick whenever the dialog is pointed at a different location.
        effect(() => {
            const currentId = this.currentLocationId();
            untracked(() => this.selectedKey.set(currentId));
        });

        // Keep the highlighted node - and the path leading to it - in sync with the picked location.
        effect(() => {
            const tree = this.locationTree();
            const key = this.selectedKey();
            untracked(() => this.revealNode(tree, key));
        });
    }

    onLocationSelectionChange(selection: TreeNode | TreeNode[] | null | undefined) {
        if (Array.isArray(selection)) {
            this.selectedLocationNode.set(selection[0] || null);
        } else {
            this.selectedLocationNode.set(selection ?? null);
        }
    }

    onLocationTreeNodeSelect(event: any) {
        const node = event.node as TreeNode;
        if (node && node.key) {
            this.selectedKey.set(node.key);
            this.selectedLocationNode.set(node);
            this.locationSelected.emit(node.key);
        }
    }

    onNodeExpand(event: any) {
        const node = event.node as TreeNode;
        if (node?.key) this.expandedKeys.add(node.key);
    }

    onNodeCollapse(event: any) {
        const node = event.node as TreeNode;
        if (node?.key) this.expandedKeys.delete(node.key);
    }

    private buildTree(): TreeNode[] {
        const locations = this.scopedLocations();

        // Map location types to icons
        const getLocationIcon = (location: LocationDto, hasChildren: boolean): string => {
            if (location.type === 'room') return 'pi pi-door-open';
            if (location.type === 'building') return 'pi pi-building';
            if (location.type === 'floor') return 'pi pi-layer-group';
            if (location.type === 'rack') return 'pi pi-server';
            return hasChildren ? 'pi pi-folder' : 'pi pi-map-marker';
        };

        const knownIds = new Set(locations.map((l) => l.id));
        const childrenOf = new Map<string, LocationDto[]>();
        const roots: LocationDto[] = [];
        locations.forEach((l) => {
            // A location whose parent is outside the current scope is rendered as a root, otherwise
            // it would silently disappear from the picker.
            if (l.parentId && knownIds.has(l.parentId)) {
                const siblings = childrenOf.get(l.parentId) ?? [];
                siblings.push(l);
                childrenOf.set(l.parentId, siblings);
            } else {
                roots.push(l);
            }
        });

        const buildNode = (location: LocationDto): TreeNode => {
            const children = (childrenOf.get(location.id) ?? []).map((child) => buildNode(child));
            return {
                key: location.id,
                label: location.name,
                data: location,
                icon: getLocationIcon(location, children.length > 0),
                children,
                leaf: children.length === 0,
                selectable: true,
                expanded: this.expandedKeys.has(location.id)
            } as TreeNode;
        };

        return roots.map((r) => buildNode(r));
    }

    /**
     * Highlights `key` and opens the branch it sits on, without closing anything the user opened.
     * Expansion is applied to the live node objects so p-tree picks it up without a rebuild.
     */
    private revealNode(tree: TreeNode[], key: string | null) {
        if (!key) {
            this.selectedLocationNode.set(null);
            return;
        }

        const findPath = (nodes: TreeNode[], path: TreeNode[] = []): TreeNode[] | null => {
            for (const node of nodes) {
                const nextPath = [...path, node];
                if (node.key === key) return nextPath;
                const found = node.children?.length ? findPath(node.children, nextPath) : null;
                if (found) return found;
            }
            return null;
        };

        const path = findPath(tree);
        if (!path) {
            // The location is not in this tree (yet) - leave what the user opened untouched.
            this.selectedLocationNode.set(null);
            return;
        }

        path.slice(0, -1).forEach((ancestor) => {
            if (ancestor.key) this.expandedKeys.add(ancestor.key);
            ancestor.expanded = true;
        });
        this.selectedLocationNode.set(path[path.length - 1]);
    }
}
